import { mediumFont, theme } from "../../../../utils/styles";

/**
 * The dialog borrows the Analysis Report screen's surface language — the same
 * glass panel, purple hairlines and gradient action — so it reads as part of
 * that page rather than as a stock MUI dialog.
 */

/** The glass surface of a field, matching the profile picker on the page. */
const fieldSurface = {
  borderRadius: "14px",
  background: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(144, 83, 246, 0.35)",
  color: "#FFFFFF",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(144, 83, 246, 0.6)",
    boxShadow: "0 0 0 3px rgba(144,83,246,0.08)",
  },
  "&.Mui-focused": {
    borderColor: "rgba(144, 83, 246, 0.8)",
    boxShadow: "0 0 0 3px rgba(144,83,246,0.15)",
  },
  "&.Mui-disabled": {
    opacity: 0.5,
    "& .MuiInputBase-input": { WebkitTextFillColor: "rgba(255,255,255,0.4)" },
  },
  "& .MuiInputBase-input": { color: "#FFFFFF" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
};

/** Autocomplete wraps its input, so the surface belongs on a descendant. */
const field = {
  width: "100%",
  "& .MuiInputBase-root": fieldSurface,
};

/**
 * Select IS the input root rather than wrapping one, so the surface has to go
 * on the element itself — a descendant selector would match nothing here.
 */
const selectField = {
  width: "100%",
  ...fieldSurface,
  "& .MuiSelect-select": { color: "#FFFFFF" },
};

const astrocartographyFilterDialogStyles = {
  paper: {
    width: "100%",
    maxWidth: "560px",
    m: 2,
    borderRadius: "28px",
    // Opaque enough to stay readable over the starfield, glassy enough to
    // match the panel underneath it.
    background:
      "linear-gradient(160deg, rgba(32,20,66,0.98) 0%, rgba(14,10,32,0.98) 100%)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(144, 83, 246, 0.35)",
    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.6)",
    backgroundImage: "none",
  },
  backdrop: {
    backgroundColor: "rgba(6, 4, 18, 0.72)",
    backdropFilter: "blur(4px)",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    px: 3,
    pt: 3,
    pb: 1,
    [theme.breakpoints.down("sm")]: { px: 2, pt: 2 },
  },
  title: {
    color: "#FFFFFF",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "20px",
    letterSpacing: "0.4px",
    [theme.breakpoints.down("sm")]: { fontSize: "18px" },
  },
  subtitle: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "13px",
    lineHeight: 1.5,
    mt: 0.5,
  },
  closeButton: {
    color: "rgba(255,255,255,0.6)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "10px",
    flexShrink: 0,
    "&:hover": {
      color: "#FFFFFF",
      background: "rgba(144,83,246,0.25)",
      borderColor: "rgba(144,83,246,0.55)",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    px: 3,
    py: 2,
    [theme.breakpoints.down("sm")]: { px: 2 },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
  },
  sectionLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  sectionIcon: {
    color: "rgba(144, 83, 246, 0.9)",
    fontSize: "20px",
  },
  sectionLabel: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "14px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    letterSpacing: "0.3px",
  },
  /** World / Continent / Country — three cells, so always an even split. */
  scopeGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 1,
    width: "100%",
    "& .MuiToggleButton-root": {
      borderRadius: "12px !important",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "rgba(255,255,255,0.65)",
      fontFamily: mediumFont.fontFamily,
      fontWeight: mediumFont.fontWeight,
      fontSize: "13px",
      textTransform: "none",
      letterSpacing: "0.3px",
      py: 1,
      transition: "all 0.2s ease",
      "&:hover": {
        background: "rgba(144,83,246,0.16)",
        borderColor: "rgba(144,83,246,0.45)",
        color: "#FFFFFF",
      },
      "&.Mui-selected": {
        background: "rgba(144,83,246,0.28)",
        borderColor: "rgba(144,83,246,0.85)",
        color: "#FFFFFF",
        boxShadow: "0 0 14px rgba(144,83,246,0.25)",
        "&:hover": { background: "rgba(144,83,246,0.34)" },
      },
    },
  },
  field,
  selectField,
  /** Dropdown surfaces are portalled, so they are styled through slot props. */
  popperPaper: {
    background: "rgba(20, 14, 44, 0.98)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(144, 83, 246, 0.35)",
    borderRadius: "14px",
    color: "#FFFFFF",
    "& .MuiAutocomplete-option": {
      fontSize: "13px",
      borderRadius: "9px",
      mx: 0.75,
      my: 0.25,
      '&[aria-selected="true"]': { background: "rgba(144,83,246,0.3)" },
      '&.Mui-focused, &[data-focus="true"]': {
        background: "rgba(144,83,246,0.18)",
      },
    },
    "& .MuiAutocomplete-groupLabel": {
      background: "transparent",
      color: "rgba(255,255,255,0.45)",
      fontSize: "11px",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      lineHeight: "28px",
    },
    "& .MuiAutocomplete-noOptions": {
      color: "rgba(255,255,255,0.45)",
      fontSize: "13px",
    },
    "& .MuiMenuItem-root": {
      fontSize: "13px",
      borderRadius: "9px",
      mx: 0.75,
      my: 0.25,
      color: "rgba(255,255,255,0.85)",
      "&:hover": { background: "rgba(144,83,246,0.18)" },
      "&.Mui-selected": {
        background: "rgba(144,83,246,0.3)",
        color: "#FFFFFF",
        "&:hover": { background: "rgba(144,83,246,0.34)" },
      },
    },
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    width: "100%",
  },
  optionHint: {
    color: "rgba(255,255,255,0.4)",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  /** Muted note under a field, e.g. the satellite/street warning. */
  hint: {
    display: "flex",
    alignItems: "flex-start",
    gap: 0.75,
    color: "rgba(255,255,255,0.45)",
    fontSize: "12px",
    lineHeight: 1.5,
    "& .MuiSvgIcon-root": { fontSize: "15px", mt: "1px", flexShrink: 0 },
  },
  summary: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    px: 1.75,
    py: 1.25,
    borderRadius: "14px",
    background: "rgba(144, 83, 246, 0.12)",
    border: "1px solid rgba(144, 83, 246, 0.3)",
    color: "rgba(255,255,255,0.75)",
    fontSize: "12.5px",
    lineHeight: 1.5,
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    py: 3,
    color: "rgba(255,255,255,0.6)",
    fontSize: "13px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 1.5,
    px: 3,
    pt: 1,
    pb: 3,
    [theme.breakpoints.down("sm")]: {
      px: 2,
      pb: 2,
      flexDirection: "column-reverse",
      alignItems: "stretch",
    },
  },
  cancelButton: {
    borderRadius: "14px",
    px: 3,
    py: "10px",
    color: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(255,255,255,0.16)",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "14px",
    textTransform: "none",
    "&:hover": {
      border: "1px solid rgba(255,255,255,0.3)",
      background: "rgba(255,255,255,0.06)",
      color: "#FFFFFF",
    },
  },
  downloadButton: {
    borderRadius: "14px",
    px: 3.5,
    py: "10px",
    minWidth: "180px",
    color: "#FFFFFF",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "14px",
    textTransform: "none",
    letterSpacing: "0.3px",
    background: "linear-gradient(90deg, #9053F6 0%, #5E75FF 55%, #2CF1DF 100%)",
    boxShadow: "0 6px 20px rgba(144, 83, 246, 0.4)",
    transition: "opacity 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      opacity: 0.9,
      boxShadow: "0 8px 28px rgba(144, 83, 246, 0.6)",
    },
    "&:disabled": {
      background: "rgba(255,255,255,0.08)",
      boxShadow: "none",
      color: "rgba(255,255,255,0.3)",
    },
  },
} as const;

export default astrocartographyFilterDialogStyles;
